import React, { Fragment, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { selectIsUserLogged } from '@selectors/auth';
import { selectMobilePlanTitle } from '@selectors/mobilePlan';

import BillingPersonalFormSection from '@routes/MobileOrderForm/PersonalDetails/BillingPersonalFormSection';

import { redirectToLoginWithTarget } from '@utils/redirects';

import { useMultiStepContext } from '@common/contexts/multiStepContext';
import Hr from '@common/components/Hr';
import { BUTTON_VARIANTS } from '@common/components/Button';
import {
  StepTitle,
  NextButton,
  ButtonText,
  AlertWrapper,
  MessageBox,
} from '../MobileOrderForm.styled';
import { FormContext } from '../FormWrapper';
import AsyncAddressForm from '@common/components/forms/AsyncAddressForm';
import {
  SectionHeadingSmall,
  SelectionControl,
} from '@common/components/forms';

import PersonalInfoFormSection from './PersonalInfoFormSection';
import ContactDetailsFormSection from './ContactDetailsFormSection';
import RoamingFormSection from './RoamingFormSection';

const SECTION_TRANSLATION_PREFIX = 'mobile.orderForm.personalDetails';

const addressFormKeyMap = {
  country: 'country',
  zipCode: 'postal_code',
  city: 'place',
  street: 'street',
  streetNr: 'house_nr',
  additionalInfo: 'suffix',
  noAdditionalStreetNumber: 'noAdditionalStreetNumber',
};

const PersonalDetails = () => {
  const router = useRouter();
  const isUserLoggedIn = useSelector(selectIsUserLogged);
  const mobilePlanTitle = useSelector(selectMobilePlanTitle);
  const {
    formik: { isValid, handleBlur, isSubmitting, values },
  } = useContext(FormContext);
  const { t } = useTranslation();
  const { handleChangeStepsValidity, next } = useMultiStepContext();
  const { formik, handleChange } = useContext(FormContext);

  useEffect(() => {
    handleChangeStepsValidity(1, isValid);
  }, [handleChangeStepsValidity, isValid]);

  return (
    <React.Fragment>
      <StepTitle>
        {t('mobile.orderForm.meta.description', '{{planName}} Abo bestellen', {
          planName: mobilePlanTitle,
        })}
      </StepTitle>
      {!isUserLoggedIn && (
        <AlertWrapper spacingTop={['scale', 0]}>
          <MessageBox
            description={t(
              `${SECTION_TRANSLATION_PREFIX}.alertCopyFromAccount.description`,
              'Wenn Du Dich anmeldest füllen wir alle möglichen Felder mit Deinen Angaben für Dich aus.',
            )}
            title={t(
              `${SECTION_TRANSLATION_PREFIX}.alertCopyFromAccount.title`,
              'Angaben von deinem VIP-Profil übernehmen?',
            )}
            buttonText={t(
              `${SECTION_TRANSLATION_PREFIX}.alertCopyFromAccount.buttonText`,
              'Einloggen',
            )}
            onButtonClick={() => redirectToLoginWithTarget(router)}
            variant="quaternary"
          />
        </AlertWrapper>
      )}
      <PersonalInfoFormSection />
      <Hr />
      <ContactDetailsFormSection />
      <Hr />
      <SectionHeadingSmall>
        {t(
          'mobile.orderForm.personalDetails.deliveryAddressSection.title',
          'Lieferadresse',
        )}
      </SectionHeadingSmall>
      <AsyncAddressForm
        formik={formik}
        formPath="delivery_address"
        formNamesMap={addressFormKeyMap}
      />
      <Hr spacings={['equal', 3]} />
      <Fragment>
        <SelectionControl
          id="billing_address.should_fill_with_delivery_data"
          name="billing_address.should_fill_with_delivery_data"
          checked={values.billing_address.should_fill_with_delivery_data}
          onChange={handleChange}
          disabled={isSubmitting}
          onBlur={handleBlur}
          variant="body2"
          label={t(
            `${SECTION_TRANSLATION_PREFIX}.fillWithDeliveryDataLabel`,
            'Gleiche Adresse für die Rechung verwenden',
          )}
        />
        {!values.billing_address.should_fill_with_delivery_data && (
          <Fragment>
            <BillingPersonalFormSection />
            <AsyncAddressForm
              formik={formik}
              formPath="billing_address"
              translationPrefix="mobile.orderForm.personalDetails.billingAddressSection"
              formNamesMap={addressFormKeyMap}
            />
          </Fragment>
        )}
      </Fragment>
      <Hr spacings={['equal', 3]} />
      <RoamingFormSection />
      <div>
        <NextButton
          onClick={next}
          type="button"
          variant={BUTTON_VARIANTS.LEVEL_1_GREEN}
          disabled={!isValid}
        >
          <ButtonText>
            {t(`${SECTION_TRANSLATION_PREFIX}.buttonNext`, 'Nächster Schritt.')}
          </ButtonText>
        </NextButton>
      </div>
    </React.Fragment>
  );
};

export default PersonalDetails;
